/**
 * AI Consensus Bot Authentication Modal
 *
 * Modal for login/register to the AI Consensus Bot backend
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../Icons';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

interface AIConsensusAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AIConsensusAuthModal({ isOpen, onClose, onSuccess }: AIConsensusAuthModalProps) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  // Handle password strength changes
  const handlePasswordStrengthChange = useCallback((_score: number, isValid: boolean) => {
    setIsPasswordValid(isValid);
  }, []);

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Connexion echouee');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }

    // Check password strength with zxcvbn
    if (!isPasswordValid) {
      setError('Le mot de passe n\'est pas assez fort. Utilisez un melange de lettres, chiffres et symboles.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await register(email, password, name || undefined);
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Inscription echouee');
      }
    } catch (err) {
      setError('Erreur lors de l\'inscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
    setName('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.95), rgba(20, 20, 30, 0.98))',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: '20px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(139, 92, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        position: 'relative',
        backdropFilter: 'blur(20px)'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: 'var(--muted)',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          <Icons.X size={20} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg, var(--primary), #8b5cf6)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Icons.Sparkles size={28} style={{ color: 'white' }} />
          </div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '6px'
          }}>
            {mode === 'login' ? 'Connexion' : 'Creer un compte'}
          </h2>
          <p style={{
            fontSize: '13px',
            color: 'var(--muted)'
          }}>
            {mode === 'login'
              ? 'Connectez-vous pour acceder a vos bots'
              : 'Inscrivez-vous pour gerer vos bots et API keys'
            }
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#ef4444',
            fontSize: '13px'
          }}>
            <Icons.AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          {/* Name Field (Register only) */}
          {mode === 'register' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--text)',
                marginBottom: '6px'
              }}>
                Nom <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optionnel)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Icons.User size={16} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted)'
                }} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 38px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: 'var(--text)',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          )}

          {/* Email Field */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: '6px'
            }}>
              Email
            </label>
            <div style={{ position: 'relative' }}>
              <Icons.Mail size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)'
              }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 38px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'var(--text)',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: mode === 'register' ? '14px' : '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: '6px'
            }}>
              Mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <Icons.Lock size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)'
              }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Min. 8 caracteres' : '••••••••'}
                required
                minLength={mode === 'register' ? 8 : undefined}
                style={{
                  width: '100%',
                  padding: '10px 38px 10px 38px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'var(--text)',
                  outline: 'none'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: '2px',
                  display: 'flex'
                }}
              >
                {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
              </button>
            </div>
            {/* Password Strength Meter (Register only) */}
            {mode === 'register' && (
              <PasswordStrengthMeter
                password={password}
                minScore={3}
                onStrengthChange={handlePasswordStrengthChange}
              />
            )}
          </div>

          {/* Confirm Password Field (Register only) */}
          {mode === 'register' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--text)',
                marginBottom: '6px'
              }}>
                Confirmer le mot de passe
              </label>
              <div style={{ position: 'relative' }}>
                <Icons.Lock size={16} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted)'
                }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 38px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: 'var(--text)',
                    outline: 'none'
                  }}
                />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p style={{
                  fontSize: '11px',
                  color: '#ef4444',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Icons.AlertCircle size={11} />
                  Les mots de passe ne correspondent pas
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '12px',
              background: isSubmitting ? 'var(--muted)' : 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isSubmitting ? (
              <>
                <Icons.Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                {mode === 'login' ? 'Connexion...' : 'Inscription...'}
              </>
            ) : (
              <>
                {mode === 'login' ? <Icons.LogIn size={16} /> : <Icons.UserPlus size={16} />}
                {mode === 'login' ? 'Se connecter' : 'Creer mon compte'}
              </>
            )}
          </button>
        </form>

        {/* Switch Mode Link */}
        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          fontSize: '13px',
          color: 'var(--muted)'
        }}>
          {mode === 'login' ? 'Pas encore de compte ? ' : 'Deja un compte ? '}
          <button
            onClick={switchMode}
            style={{
              color: 'var(--primary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '13px'
            }}
          >
            {mode === 'login' ? 'Creer un compte' : 'Se connecter'}
          </button>
        </div>

        {/* Demo Account Info */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--muted)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '6px',
            color: 'var(--primary)',
            fontWeight: 500
          }}>
            <Icons.Info size={14} />
            Compte de demonstration
          </div>
          <p>
            <strong>Email:</strong> demo@ai-consensus-bot.local<br />
            <strong>Mot de passe:</strong> demo-password
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
